import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import NotificationService, { Notification } from '../services/NotificationService';
import WebSocketService from '../services/WebSocketService';
import toast from 'react-hot-toast';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (id: number) => Promise<boolean>;
  markAsUnread: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => Promise<boolean>;
}

// Create context
const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const forceUpdateRef = useRef<number>(0);
  
  // Initial fetch of notifications
  const fetchInitialNotifications = async () => {
    try {
      setLoading(true);
      
      // Force bypass caching by adding a timestamp parameter
      const timestamp = new Date().getTime();
      // No limit - fetch all notifications for history page
      const data = await NotificationService.getUserNotifications(timestamp);
      
      if (data && Array.isArray(data)) {
        // Normalize data - ensure all notifications have required fields
        const normalizedNotifications = data.map(notification => {
          return {
            ...notification,
            id: notification.id || Math.floor(Math.random() * 100000),
            time: notification.time || formatTime(notification.created_at),
            read: notification.read_by_current_user !== undefined 
              ? notification.read_by_current_user 
              : (notification.read || false),
            message: notification.message || "New notification"
          };
        });
        
        setNotifications(normalizedNotifications);
        
        // Update unread count based on the notifications
        const unreadCount = normalizedNotifications.filter(n => !n.read).length;
        setUnreadCount(unreadCount);
        
        // Update localStorage for offline access
        localStorage.setItem('notifications', JSON.stringify(normalizedNotifications));
        localStorage.setItem('notificationCount', String(unreadCount));
      } else {
        // If API requests failed, try to get from localStorage
        const storedNotifications = localStorage.getItem('notifications');
        if (storedNotifications) {
          try {
            const parsed = JSON.parse(storedNotifications);
            setNotifications(parsed);
            const unreadCount = parsed.filter((n: Notification) => !n.read).length;
            setUnreadCount(unreadCount);
          } catch (e) {
            setNotifications([]);
            setUnreadCount(0);
          }
        } else {
          setNotifications([]);
          setUnreadCount(0);
        }
      }
      
      setError(null);
    } catch (err: any) {
      console.debug('Error fetching notifications:', err);
      setError(err?.message || 'Failed to load notifications');
      
      // Try to get from localStorage as fallback
      const storedNotifications = localStorage.getItem('notifications');
      if (storedNotifications) {
        try {
          const parsed = JSON.parse(storedNotifications);
          setNotifications(parsed);
          const unreadCount = parsed.filter((n: Notification) => !n.read).length;
          setUnreadCount(unreadCount);
        } catch (e) {
          setNotifications([]);
          setUnreadCount(0);
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Format time difference for display
  const formatTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return "Just now";
      }
      
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffDays > 0) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else if (diffHours > 0) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      } else if (diffMins > 0) {
        return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      } else {
        return "Just now";
      }
    } catch (e) {
      return "Unknown time";
    }
  };

  const markAsRead = async (id: number): Promise<boolean> => {
    try {
      const success = await NotificationService.markAsRead(id);
      
      // Update local state regardless of API success
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
      
      // Decrement unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
      return success;
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
      
      // Still update UI even if API fails
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
      
      return false;
    }
  };

  const markAsUnread = async (id: number) => {
    try {
      // This is a client-side operation as the backend might not support it
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, read: false } : n)
      );
      
      setUnreadCount(prev => prev + 1);
      return Promise.resolve();
    } catch (err: any) {
      console.error('Error marking notification as unread:', err);
      return Promise.reject(err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await NotificationService.markAllAsRead();
      
      // Update all notifications to read regardless of API success
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );
      
      // Reset unread count
      setUnreadCount(0);
      
      // Update localStorage
      localStorage.setItem('notificationCount', '0');
      localStorage.setItem('notifications', JSON.stringify(
        notifications.map(n => ({ ...n, read: true }))
      ));
      
      return Promise.resolve();
    } catch (err: any) {
      console.error('Error marking all as read:', err);
      
      // Still update UI even if API fails
      setNotifications(prev => 
        prev.map(n => ({ ...n, read: true }))
      );
      setUnreadCount(0);
      
      return Promise.resolve();
    }
  };

  const refreshNotifications = async () => {
    try {
      setLoading(true);
      
      // Force bypass caching by adding a timestamp parameter
      const timestamp = new Date().getTime();
      // Don't limit here - we want all notifications for the context
      const data = await NotificationService.getUserNotifications(timestamp);
      
      if (isMountedRef.current && data && Array.isArray(data)) {
        // Make sure all notifications have the required fields
        const normalizedNotifications = data.map(notification => {
          return {
            ...notification,
            id: notification.id || Math.floor(Math.random() * 100000),
            time: notification.time || formatTime(notification.created_at),
            read: notification.read_by_current_user !== undefined 
              ? notification.read_by_current_user 
              : (notification.read || false),
            message: notification.message || "New notification"
          };
        });
        
        setNotifications(normalizedNotifications);
        
        // Calculate new unread count
        const count = normalizedNotifications.filter(n => !n.read).length;
        setUnreadCount(count);
        setError(null);
        
        // Update localStorage
        localStorage.setItem('notifications', JSON.stringify(normalizedNotifications));
        localStorage.setItem('notificationCount', String(count));
      }
      
      // Force an update to ensure components re-render
      forceUpdateRef.current += 1;
      
      return true;
    } catch (err: any) {
      if (isMountedRef.current) {
        console.error('Error refreshing notifications:', err);
        setError(err?.message || 'Failed to load notifications');
      }
      return false;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  // Handle incoming WebSocket notification
  const handleNewNotification = (data: any) => {
    console.log('Received new notification via WebSocket:', data);
    
    if (data.notification) {
      // Add the new notification to the top of the list
      const newNotification = NotificationService.normalizeNotification(data.notification);
      
      // Check if this is a kick notification and handle it immediately
      if (newNotification.type === 'kick') {
        console.log('Received kick notification, redirecting to dashboard:', newNotification);
        
        // Show toast and redirect immediately
        toast.error('You have been removed from the round by the moderator. Redirecting to dashboard...', {
          duration: 3000,
          position: 'top-center',
          icon: '⚠️',
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          }
        });
        
        // Mark as read before redirecting
        markAsRead(newNotification.id).finally(() => {
          // Redirect after a short delay to ensure the toast is seen
          setTimeout(() => {
            console.log('Executing redirect to dashboard due to kick notification');
            window.location.href = newNotification.link || '/dashboard';
          }, 1500);
        });
        
        return;
      }
      
      // Only add if it doesn't already exist
      setNotifications(prev => {
        if (prev.some(n => n.id === newNotification.id)) {
          return prev;
        }
        return [newNotification, ...prev];
      });
      
      // Update unread count
      if (!newNotification.read) {
        setUnreadCount(prev => prev + 1);
      }
      
      // Show toast notification if visible
      if (document.visibilityState === 'visible') {
        toast.success(newNotification.message, {
          duration: 4000,
          position: 'top-center',
          icon: '🔔',
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          }
        });
      }
    }
  };

  // Handle notification read status update
  const handleNotificationUpdate = (data: any) => {
    if (data.notification_id && data.read !== undefined) {
      setNotifications(prev => 
        prev.map(n => n.id === data.notification_id ? { ...n, read: data.read } : n)
      );
      
      // Update unread count
      if (data.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        setUnreadCount(prev => prev + 1);
      }
    }
  };

  useEffect(() => {
    // Set isMounted flag
    isMountedRef.current = true;
    
    // Initial fetch
    fetchInitialNotifications();
    
    // Connect to WebSocket
    WebSocketService.connect();
    
    // Set up WebSocket message handlers
    const newNotificationHandler = WebSocketService.onMessage('notification.new', handleNewNotification);
    const updateNotificationHandler = WebSocketService.onMessage('notification.updated', handleNotificationUpdate);
    
    // Add handler for force_redirect messages
    const forceRedirectHandler = WebSocketService.onMessage('force_redirect', (data: any) => {
      console.log('Received force_redirect message:', data);
      
      // Check if this redirect is for the current user
      const currentUserId = parseInt(localStorage.getItem('userId') || '0', 10);
      
      // Convert both IDs to numbers for comparison
      const targetUserId = typeof data.user_id === 'string' ? parseInt(data.user_id, 10) : data.user_id;
      
      if (targetUserId && currentUserId && targetUserId === currentUserId) {
        // Show a toast notification
        if (data.message) {
          toast.error(data.message, {
            duration: 3000,
            position: 'top-center',
            icon: '⚠️',
            style: {
              borderRadius: '10px',
              background: '#333',
              color: '#fff',
            }
          });
        }
        
        // Redirect to the specified URL after a short delay
        setTimeout(() => {
          console.log('Redirecting to:', data.redirect_url || '/dashboard');
          window.location.href = data.redirect_url || '/dashboard';
        }, 1000);
      } else {
        console.log('Force redirect message not intended for this user', {
          messageUserId: targetUserId,
          currentUserId: currentUserId
        });
      }
    });
    
    // Set up WebSocket reconnect handler
    const connectHandler = WebSocketService.onConnect(() => {
      console.log('WebSocket connected, refreshing notifications');
      refreshNotifications();
    });
    
    // Set up visibility change listener to refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshNotifications();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Refresh notifications every 2 minutes
    const refreshInterval = setInterval(() => {
      refreshNotifications();
    }, 2 * 60 * 1000);
    
    return () => {
      isMountedRef.current = false;
      
      // Clean up WebSocket handlers
      newNotificationHandler();
      updateNotificationHandler();
      forceRedirectHandler(); // Clean up the force redirect handler
      connectHandler();
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(refreshInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      error,
      markAsRead,
      markAsUnread, 
      markAllAsRead,
      refreshNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use the notification context
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export type { Notification };